import { notifyProgress } from '@billy/design-system'
import { pdf } from '@react-pdf/renderer'
import download from 'downloadjs'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_CURRENCY } from '@features/invoices/constants/defaultCurrency'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../../../../../enums/notificationKeys'
import { usePeriodDates } from '../../../../../../../../../hooks/usePeriodDates'
import { Currency } from '../../../../../../../../../types/currency'
import { getCurrentLocale } from '../../../../../../../../../utils'
import { downloadStatusToTKeys } from '../../../../../../../../../utils/export/downloadStatusToTKeys'
import { useSalesByItemReport } from '../../../../../../../hooks/salesByItemReport/useSalesByItemReport'
import { useSalesByItemReportTableColumns } from '../../../../../../../hooks/salesByItemReport/useSalesByItemReportTableColumns'
import { useSalesByItemReportTableData } from '../../../../../../../hooks/salesByItemReport/useSalesByItemReportTableData'
import { useSalesReportFilters } from '../../../../../../../hooks/useSalesReportFilters'
import { useTotalSales } from '../../../../../../../hooks/useTotalSales'
import { getSalesByValueReportAllSummary } from '../../../../../../../utils/getSalesByValueReportAllSummary'
import { SALES_REPORT_DOWNLOAD_PROGRESS_MAX } from '../../../constants/salesReportDownloadProgressMax'
import { useSalesReportPdfStatistics } from '../../../hooks/useSalesReportPdfStatistics'
import { getSalesReportGraphImageSrc } from '../../../utils/getSalesReportGraphImageSrc'
import { SalesReportExportPdfDocument } from '../../SalesReportExportPdfDocument'

type DownloadSalesReportPdfOptions = {
  onSuccess?: () => void
  onError?: () => void
}

export const useDownloadSalesByItemReportPdf = () => {
  const [isDownloading, setIsDownloading] = useState(false)
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { salesByItemReport } = useSalesByItemReport()
  const [{ period }] = useSalesReportFilters()
  const { fromDate: startDate, toDate: endDate } = usePeriodDates(period)
  const tableData = useSalesByItemReportTableData()
  const tableColumns = useSalesByItemReportTableColumns()

  const { firstFiscalYearStart } = organization || {}
  const locale = getCurrentLocale()
  const currency = (organization?.baseCurrencyId ?? DEFAULT_CURRENCY) as Currency

  const salesByItemReportAllSummary = useMemo(
    () => getSalesByValueReportAllSummary(salesByItemReport),
    [salesByItemReport],
  )

  const { displayedTotalSales } = useTotalSales(salesByItemReportAllSummary)
  const totalSalesByItemAmount = salesByItemReport.length
  const totalStatistics = useSalesReportPdfStatistics({
    label: t('sales_report.sales_by_item.statistics.summary.total'),
    value: String(totalSalesByItemAmount),
  })

  const downloadSalesReportPdf = useCallback(
    async ({ onSuccess, onError }: DownloadSalesReportPdfOptions = {}): Promise<void> => {
      try {
        setIsDownloading(true)
        const documentTitle = t('sales_report.sales_by_item.export_file.title')
        const graphImageSrc = await getSalesReportGraphImageSrc()

        const salesReportDocument = (
          <SalesReportExportPdfDocument
            currency={currency}
            documentTitle={documentTitle}
            endDate={endDate}
            firstFiscalYearStart={firstFiscalYearStart}
            footerLabel={t('sales_report.sales_by_item.table.footer.total_sales')}
            graphImageSrc={graphImageSrc}
            locale={locale}
            organizationName={organization?.name}
            startDate={startDate}
            statistics={totalStatistics}
            tableColumns={tableColumns}
            tableData={tableData}
            totalSales={displayedTotalSales}
          />
        )
        const blob = await pdf(salesReportDocument).toBlob()
        download(blob, documentTitle)

        const successTKeys = downloadStatusToTKeys.success
        notifyProgress({
          closable: true,
          description: t(successTKeys.description),
          id: NotificationKeys.SalesReportDownloadPdf,
          progress: SALES_REPORT_DOWNLOAD_PROGRESS_MAX,
          title: t(successTKeys.title),
          variant: 'success',
        })

        onSuccess?.()
      } catch {
        const errorTKeys = downloadStatusToTKeys.error
        notifyProgress({
          closable: true,
          description: t(errorTKeys.description),
          id: NotificationKeys.SalesReportDownloadPdf,
          progress: SALES_REPORT_DOWNLOAD_PROGRESS_MAX,
          title: t(errorTKeys.title),
          variant: 'error',
        })
        onError?.()
      } finally {
        setIsDownloading(false)
      }
    },
    [
      currency,
      displayedTotalSales,
      endDate,
      firstFiscalYearStart,
      locale,
      organization?.name,
      startDate,
      t,
      tableColumns,
      tableData,
      totalStatistics,
    ],
  )

  return { download: downloadSalesReportPdf, isDownloading }
}
